<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <div class="d-flex mb-8 align-center">
        <h2 class="font-weight-bold">{{ $t('carbonCredit.manageTrees.bulkTreeDataImport') }}</h2>
        <v-spacer></v-spacer>
        <v-btn depressed color="primary" class="mr-5" outlined @click="$router.back()">
          {{ $t('back') }}
        </v-btn>
      </div>

      <v-dialog max-width="600" v-model="successDialog" @click:outside="closeDialog">
        <v-card rounded="0">
          <v-toolbar color="primary" class="white--text" flat :elevation="0">
            <v-toolbar-title class="font-weight-bold">
              {{ $t('success') }}
            </v-toolbar-title>
            <v-btn icon @click="closeDialog" color="white" class="ml-auto">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pt-5 text-center">
            <p class="text-center black--text text-body-1 mt-3">
              {{ $t('carbonCredit.manageTrees.bulkImportedSuccessMessage', { count: successRecordsCount }) }}
            </p>
          </v-card-text>
        </v-card>

        <!-- </template> -->
      </v-dialog>

      <v-card v-if="bulkImportDetail" elevation="0" class="px-5">
        <v-card-text class="text-body-1">
          <p class="grey--text">
            {{ $t('farm.importCompleted') }}
          </p>
          <div class="d-flex align-center">
            <v-progress-linear rounded height="8" value="100" color="primary"></v-progress-linear>
            <span class="primary--text font-weight-bold ml-2">100%</span>
          </div>
          <p class="mt-2">
            {{ successRecordsCount }} {{ $t('of') }} {{ bulkImportDetail.totalRecordsCount }}
            {{ $t('recordsImported') }}
          </p>

          <v-tabs class="tabs-pill mb-5" background-color="grey lighten-3" icons-and-text fixed-tabs v-model="tab">
            <v-tab @click="tab = 'all'" class="flex-column" style="max-width: 100%; margin-left: 0">
              <span class="text-h6">{{ bulkImportDetail.totalRecordsCount }}</span>
              <span style="text-transform: none">{{ $t('totalRecords') }}</span>
            </v-tab>
            <v-tab @click="tab = 'success'" class="flex-column" style="max-width: 100%">
              <span class="text-h6">{{ successRecordsCount }}</span>
              <span style="text-transform: none">{{ $t('success') }}</span>
            </v-tab>
            <v-tab @click="tab = 'failed'" class="flex-column" style="max-width: 100%; margin-right: 0">
              <span class="text-h6">{{ bulkImportDetail.failedRecordsCount }}</span>
              <span style="text-transform: none">{{ $t('failed') }}</span>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <v-tab-item key="all">
              <v-data-table
                disable-pagination
                :headers="tableHeaders"
                :items="bulkImportDetail.records"
                hide-default-footer
              >
                <template v-slot:item.farm.farmName="{ item }">
                  {{ item.farm?.farmName || item.payloadJsonData.farm_id || '' }}
                </template>
                <template v-slot:item.treeType.name="{ item }">
                  {{ item.treeType?.name || item.payloadJsonData.tree_type_name || '' }}
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip
                    style="width: 100px"
                    class="justify-center"
                    :color="item.status.toLowerCase() === 'success' ? 'primary' : 'error'"
                    outlined
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:item.action="{ item }">
                  <div>
                    <v-btn
                      v-if="item.status.toLowerCase() === 'success'"
                      icon
                      elevation="2"
                      class="mr-2"
                      :to="{ name: 'editTree', params: { id: item.recordedJsonData.id } }"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="item.status.toLowerCase() === 'failed'"
                      @click="deleteRecord(item.id)"
                      icon
                      elevation="2"
                    >
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-tab-item>
            <v-tab-item key="success">
              <v-data-table disable-pagination :headers="tableHeaders" :items="successRecords" hide-default-footer>
                <template v-slot:item.farm.farmName="{ item }">
                  {{ item.farm?.farmName || item.payloadJsonData.farm_id || '' }}
                </template>
                <template v-slot:item.treeType.name="{ item }">
                  {{ item.treeType?.name || item.payloadJsonData.tree_type_name || '' }}
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip style="width: 100px" class="justify-center" color="primary" outlined>
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:item.action="{ item }">
                  <div>
                    <v-btn
                      :to="{ name: 'editTree', params: { id: item.recordedJsonData.id } }"
                      icon
                      elevation="2"
                      class="mr-2"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-tab-item>
            <v-tab-item key="failed">
              <v-data-table disable-pagination :headers="tableHeaders" :items="failedRecords" hide-default-footer>
                <template v-slot:item.farm.farmName="{ item }">
                  {{ item.farm?.farmName || item.payloadJsonData.farm_id || '' }}
                </template>
                <template v-slot:item.treeType.name="{ item }">
                  {{ item.treeType?.name || item.payloadJsonData.tree_type_name || '' }}
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip style="width: 100px" class="justify-center" color="error" outlined>
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:item.action="{ item }">
                  <v-btn @click="deleteRecord(item.id)" icon elevation="2">
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-tab-item>
          </v-tabs-items>

          <v-divider class="my-5 px-0"></v-divider>
          <v-card-actions class="px-0">
            <v-spacer></v-spacer>
            <v-btn :to="{ name: 'bulkImportTrees' }" width="112" color="primary" class="btn-11 btn-22">
              {{ $t('continue') }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import loadingMixin from '@/mixins/LoadingMixin';
import axios from 'axios';

export default {
  data() {
    return {
      breadcrumbs: [
        { text: 'Dashboard', disabled: false, href: '/dashboard', exact: true },
        { text: 'Manage Trees', disabled: false, href: '/manage-trees' },
        { text: 'Bulk Tree Data Import', disabled: false, href: '/manage-trees/bulk-imports' },
      ],
      bulkImportDetail: null,
      tab: 'all',
      tableHeaders: [
        {
          text: this.$t('carbonCredit.manageTrees.treeId'),
          align: 'start',
          value: 'payloadJsonData.tree_id_by_user',
          class: 'black--text',
        },
        {
          text: this.$t('carbonCredit.user.farm'),
          align: 'start',
          value: 'farm.farmName',
          class: 'black--text',
        },
        {
          text: this.$t('carbonCredit.manageTrees.treeType'),
          align: 'start',
          value: 'treeType.name',
          class: 'black--text',
        },
        {
          text: this.$t('oma.status'),
          align: 'center',
          value: 'status',
          class: 'black--text',
        },
        {
          text: this.$t('oma.action'),
          align: 'center',
          value: 'action',
          class: 'black--text',
          width: '120px',
        },
      ],
      successDialog: false,
    };
  },

  mixins: [loadingMixin],

  async mounted() {
    this.startLoading();
    await this.fetchBulkImportDetail(this.$route.params.id);
    if(this.$route.query.imported && this.successRecordsCount > 0) {
      this.successDialog = true;
    }
    this.stopLoading();
  },

  computed: {
    successRecordsCount() {
      return this.bulkImportDetail
        ? this.bulkImportDetail.totalRecordsCount - this.bulkImportDetail.failedRecordsCount
        : 0;
    },
    failedRecords() {
      if (!this.bulkImportDetail) return [];
      return this.bulkImportDetail.records.filter((record) => record.status === 'FAILED');
    },
    successRecords() {
      if (!this.bulkImportDetail) return [];
      return this.bulkImportDetail.records.filter((record) => record.status === 'SUCCESS');
    },
  },

  methods: {
    closeDialog() {
      this.successDialog = false;
    },
    async fetchBulkImportDetail(id) {
      try {
        const response = await axios.get(`/manage-trees/bulk-import/${id}`);
        const data = response.data;
        if (data.success) {
          this.bulkImportDetail = data.data;
        } else {
          this.$notify({
            type: 'error',
            title: data.message || this.$t('serverError'),
          });
        }
      } catch (error) {
        this.$notify({
          type: 'error',
          title: this.$t('serverError'),
        });
      }
    },

    async deleteRecord(recordId) {
      try {
        this.startLoading();
        const response = await axios.delete(`/manage-trees/bulk-import/records/${recordId}`);
        const data = response.data;
        if (data.success) {
          this.bulkImportDetail = Object.assign({}, this.bulkImportDetail, {
            ...data.data.myTreeBulkImport,
            records: this.bulkImportDetail.records.filter((record) => record.id != recordId),
          });
        } else {
          throw new Error(data.message);
        }
      } catch (error) {
        console.error(error);
        this.$notify({
          type: 'error',
          title: this.$t('failed'),
        });
      } finally {
        this.stopLoading();
      }
    },
  },
};
</script>
